import type { Actor } from "@baton/api";
import { useI18n } from "@baton/i18n";
import { Bot } from "@baton/icons";
import { Avatar as Primitive } from "radix-ui";
import { cn } from "../../../cn.ts";
import { Badge } from "../Badge/Badge.tsx";
import { initialsOf } from "./initials.ts";
import "./Avatar.css";

/**
 * FR-015-043, § Primitives, Avatar, constitution § I, taken from shadcn/ui's Avatar over Radix
 * (Spectrum UI has a stack but no single avatar) and themed through the variables of
 * `packages/tokens` (2026-09-15, by the owner's decision): the image and initials fallback,
 * the agent ring, the desaturated deactivated state with its title. Every actor renders from
 * the same `actor` shape, so an agent cannot be forgotten.
 *
 * What differs from the source, and why: five sizes read from the avatar tokens rather than
 * three fixed ones; the fill and the initials' colour sit on the root rather than the
 * fallback, so a list row can set the initials in the primary text colour
 * (`contracts/design-system.md` § Data display, ItemRow); the agent ring is drawn inside the
 * avatar's edge in the agent colour, so an agent's avatar keeps a person's size; and the
 * initials are a fixed share of the disc at one weight on every size (`Avatar.css`, 2026-09-18).
 */
export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface AvatarActor extends Actor {
  readonly is_active?: boolean;
}

export interface AvatarProps {
  readonly actor: AvatarActor;
  readonly size?: AvatarSize;
  readonly showRing?: boolean;
  /**
   * An agent is marked by a small badge on the avatar's corner, the robot in Agent Violet, in place of
   * the ring and of the `AgentBadge` beside its name (2026-10-03, by the owner's approval of the
   * product review: a one-line row has no room for the pill); the agent's kind is in its accessible
   * name and its tooltip. A person's avatar draws no badge.
   */
  readonly agentBadge?: boolean;
  /**
   * The name is written beside the disc, so the disc is hidden from assistive technology rather than
   * read as the same name twice (2026-10-21, found by 028's screen-reader pass of the Tickets lists).
   */
  readonly decorative?: boolean;
  readonly className?: string;
}

/* The disc per size; the initials' type follows the disc in `Avatar.css`. */
const sizes: Readonly<Record<AvatarSize, string>> = {
  xs: "size-(--avatar-xs)",
  sm: "size-(--avatar-sm)",
  md: "size-(--avatar-md)",
  lg: "size-(--avatar-lg)",
  xl: "size-(--avatar-xl)",
};

export function isDeactivated(actor: AvatarActor): boolean {
  return actor.is_active === false;
}

/**
 * A stable hue from the actor's id, so the same person is the same colour on every screen; the
 * initials sit white on it. The lightness is the step at which white reaches 4.5:1 on every hue of
 * this chroma: at 0.58 the greens and teals reached 4.0 and the initials failed SC 1.4.3
 * (2026-09-21, FR-015-100). Exported as `avatarTone` (2026-10-01) for the meeting page's speaker
 * strip, which draws each speaker's talk in the colour of their avatar.
 */
const TONE_LIGHTNESS = 0.52;

export function avatarTone(id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return `oklch(${String(TONE_LIGHTNESS)} 0.14 ${String(hash % 360)})`;
}

export function Avatar({ actor, size = "md", showRing = true, agentBadge = false, decorative = false, className }: AvatarProps) {
  const { t } = useI18n();
  const deactivated = isDeactivated(actor);
  const avatarUrl = actor.avatar_url ?? null;
  const agent = actor.kind === "agent";
  const badged = agent && agentBadge;
  const name = deactivated ? t("ui.actor.deactivated", { name: actor.display_name }) : actor.display_name;
  // A badged agent's name carries its kind, which the badge stands for in place of the words beside it.
  const label = agent ? (badged && actor.agent_kind !== undefined && actor.agent_kind !== null ? t("ui.actor.agentKindName", { name, kind: actor.agent_kind }) : t("ui.actor.agentName", { name })) : name;
  const root = (
    <Primitive.Root
      data-slot="avatar"
      data-size={size}
      data-agent={agent ? "true" : undefined}
      data-deactivated={deactivated ? "true" : undefined}
      className={cn("group/avatar relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary align-middle text-secondary-foreground select-none", sizes[size], agent && showRing && !badged && "outline-2 -outline-offset-2 outline-agent", deactivated && "opacity-70 grayscale", className)}
      title={deactivated ? name : undefined}
      {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": label })}
    >
      {avatarUrl !== null ? (
        <>
          <Primitive.Image data-slot="avatar-image" className="aspect-square size-full object-cover" src={avatarUrl} alt="" />
          <Primitive.Fallback data-slot="avatar-fallback" className="flex size-full items-center justify-center rounded-full" delayMs={300} aria-hidden="true">
            {fallbackFor(actor, size)}
          </Primitive.Fallback>
        </>
      ) : (
        <span data-slot="avatar-fallback" className="flex size-full items-center justify-center rounded-full text-text-on-solid" style={deactivated ? undefined : { background: avatarTone(actor.id) }} aria-hidden="true">
          {fallbackFor(actor, size)}
        </span>
      )}
    </Primitive.Root>
  );
  if (!badged) return root;
  return (
    <span data-slot="avatar-badged" className="relative inline-flex shrink-0" title={label}>
      {root}
      <span data-slot="agent-mark" className="absolute -end-1 -bottom-1 flex size-(--agent-mark-size) items-center justify-center rounded-full border border-background bg-agent text-text-on-solid [&>svg]:size-2" aria-hidden="true">
        <Bot />
      </span>
    </span>
  );
}

/** The initials, or one initial on the 16 and 20 px discs, where two letters at the micro step would touch the circle (2026-09-17; the 20 px disc since 2026-10-10, when the initials took the micro step as their floor). */
function fallbackFor(actor: AvatarActor, size: AvatarSize) {
  if (actor.kind === "agent" && actor.display_name.trim().length === 0) return <Bot className="size-3/5" />;
  const initials = initialsOf(actor.display_name);
  return size === "xs" || size === "sm" ? initials.slice(0, 1) : initials;
}

/**
 * The word "agent" and the kind, beside the colour (SC 1.4.1, § Meaning colours), where a table, a
 * picker or a notice names an actor: the quiet Badge with the robot in Agent Violet and the words in
 * sentence case ("Testing agent"), with no fill (2026-10-03, by the owner's approval of the product
 * review, which replaced the soft purple upper-case pill).
 */
export function AgentBadge({ actor }: { readonly actor: Actor }) {
  const { t } = useI18n();
  if (actor.kind !== "agent") return null;
  return (
    <Badge accent="purple" icon={<Bot />}>
      <span className="inline-block first-letter:uppercase">{actor.agent_kind === undefined || actor.agent_kind === null ? t("ui.actor.agent") : t("ui.actor.agentKind", { kind: actor.agent_kind })}</span>
    </Badge>
  );
}
